/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import css from '@emotion/css/macro'
import { connect } from 'react-redux'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import { t } from '@sportninja/common/i18n'
import teamActions from '@sportninja/common/actions/team'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'

import { playerForm } from '../../components/Form/form-configs'
import { SheetInnerForm } from '../../components/Form/FormSheet'
import Sheet from '../../components/Sheet'
import { SearchMenu } from '../../components/SearchMenu'
import { getSearchTypes } from '../../components/SearchMenu/search-menu-helpers'
import { Flex } from '../../components/Layout'
import { FormButton } from '../../components/Form/css'
import { FollowBtnButton } from '../../components/FollowBtn'
import NewButton from '../../components/Page/NewButton'

const PlayerSearchResult = ({ onClick }) => {
  return (
    <FollowBtnButton onClick={onClick}>{t('common:select')}</FollowBtnButton>
  )
}

const SearchStep = ({
  isHidden,
  onCreateClick,
  onResultClick,
  players,
  orgId,
  orgName,
}) => {
  const searchTypes = getSearchTypes()

  return (
    <Flex
      column
      css={css`
        max-height: 500px;
        display: ${isHidden ? 'none' : 'flex'};
      `}
    >
      <SearchMenu
        alwaysShowPager
        noResultsText={`Search for players in the "${orgName}" ${t(
          'common:organization'
        )}`}
        noEmptySearch
        searchTypes={{
          [ENTITY_TYPES.player]: {
            ...searchTypes[ENTITY_TYPES.player],
            searchText: 'Search for an existing player to add',
            isAdminSearch: true,
            slug: 'search/admin/players',
            // this endpoint requires the organization of the team to be sent along
            query: `&organization=${orgId}`,
          },
        }}
        onResultClick={onResultClick}
        ResultButton={({ result }) => (
          <PlayerSearchResult onClick={() => onResultClick(result)} />
        )}
        filter={(result) => {
          if (!Array.isArray(players)) return true
          return players.findIndex((p) => p.id === result.id) === -1
        }}
      />
      <p
        css={css`
          margin-top: 8px;
          margin-bottom: 20px;
          text-align: center;
        `}
      >
        - OR -
      </p>
      <FormButton onClick={onCreateClick}>Create a new player</FormButton>
    </Flex>
  )
}

const InnerCreatePlayer = ({
  addExistingPlayer,
  createPlayer,
  isOpen,
  orgId,
  orgName,
  players,
  toggleOpen,
  hasAdminPermission = false,
  creatingPlayersRequiresEmail = false,
  sportName = 'hockey',
}) => {
  const [player, setPlayer] = useState({})
  const [step, setStep] = useState(0)

  useEffect(() => {
    if (!isOpen) setStep(0)
  }, [isOpen])

  const useExistingPlayer = Object.keys(player).length > 0

  return (
    <>
      <SearchStep
        isHidden={step !== 0}
        onCreateClick={() => setStep(1)}
        onResultClick={(result) => {
          setPlayer(result)
          setStep(1)
        }}
        players={players}
        orgId={orgId}
        orgName={orgName}
      />
      <div
        css={css`
          display: ${step === 0 ? 'none' : 'block'};
        `}
      >
        {step !== 0 && (
          <SheetInnerForm
            disableToggleOnCancel
            form={playerForm(
              player,
              useExistingPlayer,
              creatingPlayersRequiresEmail,
              hasAdminPermission,
              sportName
            )}
            onCancel={() => {
              setPlayer({})
              setStep(0)
            }}
            onSubmit={
              useExistingPlayer
                ? addExistingPlayer.bind(this, player.id)
                : createPlayer
            }
            toggleOpen={toggleOpen}
          />
        )}
      </div>
    </>
  )
}

const CreatePlayerSheet = ({
  sportName = 'hockey',
  teamId,
  orgId,
  orgName,
  rosterId,
  players = [],
  hasAdminPermission = false,
  creatingPlayersRequiresEmail = false,
  onSuccess,

  // Redux
  addExistingPlayer,
  createPlayer,
}) => {
  return (
    <Sheet
      button={(toggleOpen) => {
        return <NewButton onClick={toggleOpen} />
      }}
      title={t('Web:addPlayer')}
    >
      {(toggleOpen, setDirty, isOpen) => (
        <InnerCreatePlayer
          sportName={sportName}
          addExistingPlayer={async (...args) => {
            const response = await addExistingPlayer(teamId, rosterId, ...args)
            await onSuccess()
            return response
          }}
          createPlayer={async (...args) => {
            const response = await createPlayer(teamId, rosterId, ...args)
            await onSuccess()
            return response
          }}
          isOpen={isOpen}
          toggleOpen={toggleOpen}
          orgId={orgId}
          orgName={orgName}
          players={players}
          hasAdminPermission={hasAdminPermission}
          creatingPlayersRequiresEmail={creatingPlayersRequiresEmail}
        />
      )}
    </Sheet>
  )
}

const mapDispatchToProps = (dispatch) => ({
  createPlayer: bindActionToPromise(
    dispatch,
    teamActions.roster.player.create.request
  ),
  addExistingPlayer: bindActionToPromise(
    dispatch,
    teamActions.roster.player.update.request
  ),
})

export default connect(null, mapDispatchToProps)(CreatePlayerSheet)
